import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { FolderDeletable, getFolderDescendantFolders } from '@core/util/folder';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { stringToLowerCaseStringArray } from '@core/util/string';
import {
	Context,
	DefaultFolder,
	Folder,
	Document as MatterDocument,
	MatterFileType,
} from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store } from '@ngxs/store';
import { MatterFoldersService } from '@repos/folders/matter-folders.service';
import { SetExpandAllDocuments } from '@state/user-profile/actions';
import { DropdownOption } from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import { SelectionGroup } from '../../atomic/atoms/dropdown-options/dropdown-options.component';
import { UserProfileState } from '@state/user-profile';
import { BusinessDocumentV2, DocumentV2 } from '@models/documents';

export type FileListSortColumn = 'updatedAt' | 'title' | 'lastEditedBy';
export type FileListSortOrder = 'asc' | 'desc';

export interface Authors {
	[key: string]: string;
}

export interface EditDocumentMeta {
	documentId: string;
	folderId: string;
	context?: Context;
}

interface FileListFolder extends Folder {
	children: number;
	expanded: boolean;
	updatedAt: number;
}

const DefaultFileListFolder: FileListFolder = {
	...DefaultFolder,
	children: 0,
	expanded: false,
	updatedAt: 0,
};

enum FOLDER_ACTIONS {
	ADD_FOLDER,
	ADD_DOCUMENT,
	ADD_BLANK_DOCUMENT,
	DELETE_FOLDER,
	EDIT_FOLDER,
	TOGGLE_EXPAND,
	COLLAPSE_AND_UNSET_ALL,
}

enum FILE_ACTIONS {
	COPY,
	EDIT_TEMPLATE,
	EDIT_SETTINGS,
	MOVE,
	RESET,
	DELETE,
}

// move single file to unknown folder
export type MoveDocumentEvent = string;

// move multiple files to known folder
export interface MoveDocumentsToFolderEvent {
	documents: (MatterDocument | DocumentV2)[];
	folderId: string;
}

type SelectedDocuments = Set<string>;
type TitleSearchFn = (value: Folder | MatterDocument) => boolean;

export type CanCreateFolder = (inFolder: Folder) => boolean;

export type CanMoveDocument = (document: MatterDocument) => boolean;

export type CanMoveDocumentToFolder = (
	document: MatterDocument,
	folder: Folder,
) => boolean;

const titleSearchFactory = (search: string): TitleSearchFn => {
	const words = stringToLowerCaseStringArray(search);
	if (words.length) {
		return (value: Folder | MatterDocument) =>
			words.every((word) =>
				(value.title || '').toLocaleLowerCase().includes(word),
			);
	}

	return () => false;
};

export enum DOCUMENT_TREE_MODE {
	MANAGE,
	MATTER,
}

export interface SortChange {
	column: FileListSortColumn;
	order: FileListSortOrder;
}

@Component({
	selector: 'app-document-tree-word',
	templateUrl: './document-tree-word.component.html',
	styleUrls: ['./document-tree-word.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentTreeWordComponent implements OnChanges, OnInit {
	// holds property names of our instance that should trigger
	// a refilter/render
	static updateListOnChangeKeys: (keyof DocumentTreeWordComponent)[] = [
		'documents',
		'filter',
		'folders',
		'matterType',
	];

	static documentsDragged: MatterDocument[];
	static documentSelection: SelectedDocuments;

	private isSingleClicking = false;
	private activeHoveringFolderId!: string;
	private lastFileClicked!: MatterDocument;
	public DocumentTreeModes = DOCUMENT_TREE_MODE;

	@Input() doubleClickDelayMs = 250;
	@Input() folderHoverToExpandDelayMs = 500;
	@Input() forceExpandOnDragHover = true;
	@ViewChild(MatMenuTrigger) actionsTrigger: MatMenuTrigger;
	@Output() addDocument = new EventEmitter<string>();
	@Output() addBlankDocument = new EventEmitter<string>();
	@Output() addDocumentFromTemplate = new EventEmitter<string>();
	@Output() addFolder = new EventEmitter<string>();
	@Input() authors: Authors = null;
	@Output() copyDocument = new EventEmitter<string>();
	@Input() currentFolderId = '';
	@Output() deleteDocument = new EventEmitter<string>();
	@Output() deleteFolder = new EventEmitter<string>();
	@Input() documents: MatterDocument[] = [];
	@Output() editDocument = new EventEmitter<EditDocumentMeta>();
	@Output() editDocumentSettings = new EventEmitter<string>();
	@Output() editFolder = new EventEmitter<string>();
	@Input() filter = '';
	filteredDocuments: MatterDocument[] = [];
	filteredFolders: FileListFolder[] = [];
	folderDocuments: MatterDocument[];
	@Input() folders: Folder[] = [];
	@Input() matterType: MatterFileType = null;
	@Input() mode: DOCUMENT_TREE_MODE = DOCUMENT_TREE_MODE.MATTER;
	@Output() moveDocument = new EventEmitter<MoveDocumentEvent>();
	@Output() moveDocumentsToFolder =
		new EventEmitter<MoveDocumentsToFolderEvent>();
	@Output() resetDocument = new EventEmitter<string>();
	@Input() root = false;
	@Input() parentId = '';
	@Output() selectedDocuments = new EventEmitter<string[]>();
	@Output() sortChange: EventEmitter<SortChange> =
		new EventEmitter<SortChange>();
	@Input() showHeaders = true;
	@Input() showTypeTags = false;
	@Input() showVersions = false;
	//
	@Input() sortBy: FileListSortColumn = 'title';
	@Input() sortOrder: FileListSortOrder = 'asc';

	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	folderItemGroups: {
		[folderId: string]: { items: DropdownOption<FOLDER_ACTIONS> }[];
	} = {};
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	folderExpandItem: {
		[folderId: string]: { items: DropdownOption<FOLDER_ACTIONS> }[];
	} = {};
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	fileItemGroups: {
		[fileId: string]: { items: DropdownOption<FOLDER_ACTIONS> }[];
	} = {};

	ICON = ICON;
	ICON_STYLE = ICON_STYLE;

	get MatterFileType(): typeof MatterFileType {
		return MatterFileType;
	}

	get draggedDocuments(): MatterDocument[] {
		return DocumentTreeWordComponent.documentsDragged;
	}

	set draggedDocuments(document: MatterDocument[]) {
		DocumentTreeWordComponent.documentsDragged = document;
	}

	get documentSelection(): SelectedDocuments {
		return DocumentTreeWordComponent.documentSelection;
	}

	documentIdsBeingDragged = new Set<string>();

	@Input() canCreateFolder: CanCreateFolder = () => true;

	@Input() canMoveDocument: CanMoveDocument = () => true;

	@Input() canMoveDocumentToFolder: CanMoveDocumentToFolder = () => true;

	@SelectSnapshot(UserProfileState.documentsSettingsExpandAll)
	documentsExpandAll!: boolean;

	constructor(
		private matterFoldersService: MatterFoldersService,
		private cdr: ChangeDetectorRef,
		private store: Store,
	) {}

	ngOnInit(): void {
		if (this.root) {
			DocumentTreeWordComponent.documentSelection = new Set();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			DocumentTreeWordComponent.updateListOnChangeKeys.some(
				(key) => key in changes,
			)
		) {
			// when documents of folders change
			// add `folderId` to documents that are missing one
			this.folderDocuments = this.documents.map((document) => ({
				...document,
				folderId: document.folderId
					? document.folderId
					: document.groupType
						? this.folders.find((folder) => folder.type === document.groupType)
								?.id
						: '',
			}));
			this.updateFileList();
		}
	}

	onExpandAllClick(): void {
		const expanded = this.documentsExpandAll;
		this.store.dispatch(new SetExpandAllDocuments(!expanded));
		if (expanded) {
			this.filteredFolders
				.filter((folder) => folder.expanded)
				.forEach((folder) => this.toggleFolderExpansion(folder));
		}
		this.updateFileList();
	}

	collapseAndUsetExpandAll(): void {
		this.store.dispatch(new SetExpandAllDocuments(false));
	}

	/**
	 * Update the sort order for the file list.
	 * @param sortBy
	 * @param sortOrder
	 */
	changeSort(sortBy: FileListSortColumn, sortOrder: FileListSortOrder): void {
		if (this.sortBy === sortBy) {
			// only change the order if the sort column hasn't changed
			this.sortOrder = sortOrder;
		}
		this.sortBy = sortBy;
		this.updateFileList();
		this.sortChange.emit({
			column: sortBy,
			order: sortOrder,
		});
	}

	clearSelected(): void {
		DocumentTreeWordComponent.documentSelection.clear();
		this.cdr.detectChanges();
		this.emitSelectedDocuments();
	}

	emitAddDocument(documentOrFolderId: string): void {
		this.addDocument.emit(documentOrFolderId);
	}

	emitAddBlankDocument(folderId: string): void {
		this.addBlankDocument.emit(folderId);
	}

	emitAddDocumentFromTemplate(folderId: string): void {
		this.addDocumentFromTemplate.emit(folderId);
	}

	emitAddFolder(folderId: string): void {
		this.addFolder.emit(folderId);
	}

	emitCopyDocument(documentId: string): void {
		this.copyDocument.emit(documentId);
	}

	emitDeleteDocument(documentId: string): void {
		this.deleteDocument.emit(documentId);
	}

	emitDeleteFolder(folderId: string): void {
		this.deleteFolder.emit(folderId);
	}

	emitEditDocument(documentMeta: EditDocumentMeta): void {
		this.editDocument.emit(documentMeta);
	}

	onEditDocumentSettigns(documentId: string): void {
		this.editDocumentSettings.emit(documentId);
	}

	emitEditFolder(folderId: string): void {
		this.editFolder.emit(folderId);
	}

	emitEditDocumentSettings(documentId: string): void {
		this.editDocumentSettings.emit(documentId);
	}

	emitMoveDocument(documentId: string): void {
		const { canMoveDocument } = this;
		if (canMoveDocument(this.getDocumentById(documentId))) {
			this.moveDocument.emit(documentId);
		}
	}

	emitMoveDocumentsToFolder(evt: MoveDocumentsToFolderEvent): void {
		this.moveDocumentsToFolder.emit(evt);
	}

	emitResetDocument(documentId: string): void {
		this.resetDocument.emit(documentId);
	}

	emitSelectedDocuments(): void {
		// we operate on a shared selection tree now, so
		// the argument isn't needed UNLESS we're at the top
		// of the stack (first component to bind)
		const allIds = [...this.selectedDocumentIds];
		this.selectedDocuments.emit(allIds);
	}

	expandFolder(folderId: string): void {
		const folder = this.filteredFolders.find((f) => f.id === folderId);
		if (folder && !folder.expanded) {
			this.toggleFolderExpansion(folder);
		}
	}

	getDocumentById(documentId: string): MatterDocument {
		return this.documents.find((document) => document.id === documentId);
	}

	isFileSelectorDisabled(documentId: string): boolean {
		const selectedIds = this.selectedDocumentIds;
		const count = selectedIds.size;

		return count > 1 || (count === 1 && !selectedIds.has(documentId));
	}

	toggleFolderExpansion(folder: FileListFolder): void {
		folder.expanded = !folder.expanded;
		this.matterFoldersService.ToggleExpandedFolder(folder.id);
	}

	toggleMultipleFileSelection(ids: string[]): void {
		ids?.forEach((id) => DocumentTreeWordComponent.documentSelection.add(id));
		this.emitSelectedDocuments();
	}

	toggleFileSelection({ id }: MatterDocument): void {
		const selections = DocumentTreeWordComponent.documentSelection;
		if (selections.has(id)) {
			selections.delete(id);
		} else {
			selections.add(id);
		}
		this.emitSelectedDocuments();
	}

	trackById(index: number, item: MatterDocument | Folder): string {
		return item.id;
	}

	/**
	 * Return documents that belong to the specified matter type and (optionally) have the search text in the title.
	 * @param documents
	 * @param matterType
	 * @private
	 */
	private filterDocuments(
		documents: MatterDocument[],
		matterType?: MatterFileType,
		filter: string = '',
	): MatterDocument[] {
		const isSearching = stringToLowerCaseStringArray(filter).length;
		const titleSearchFn = titleSearchFactory(filter);
		const selectedDocuments = this.selectedDocumentIds;
		const isManage = this.mode === DOCUMENT_TREE_MODE.MANAGE;

		return documents
			.filter(
				(document) =>
					// always return selected documents, regardless of filters
					selectedDocuments.has(document.id) ||
					// otherwise if we are filtering on types, return those
					((isManage
						? matterType
							? document.matterType?.includes(matterType)
							: !document.matterType?.length
						: true) &&
						// and, if we are searching, ensure a match
						(isSearching ? titleSearchFn(document) : true)),
			)
			.sort((prev, curr) =>
				this.sortList(prev, curr, this.sortBy, this.sortOrder),
			);
	}

	/**
	 * Return all folders for the specified matter type.
	 * @param folders
	 * @param matterType
	 * @param activeSearch string representing a search value
	 * @param filteredDocuments if there is an activeSearch, a list of our document matches
	 * @private
	 */
	private filterFolders(
		folders: FileListFolder[],
		matterType?: MatterFileType,
		activeSearch?: string,
		filteredDocuments?: MatterDocument[],
	): FileListFolder[] {
		let forceFilteredFolders: Set<string> = new Set();
		const titleSearchFn = titleSearchFactory(activeSearch);
		const directFolderMatches = folders.filter((folder) =>
			titleSearchFn(folder),
		);
		if (activeSearch) {
			// ensure our matched parent folders are all flagged
			// OR, folders that directly match the search
			const matchedFolders = [
				...filteredDocuments
					.map((doc) => doc.folderId)
					.map((folderId) => folders.find((f) => f.id === folderId)),
				...directFolderMatches,
			];

			forceFilteredFolders = new Set(
				matchedFolders
					.concat(
						...matchedFolders.map((folder) =>
							this.getAncestorFolders(folder, folders),
						),
					)
					.filter((folder) => !!folder)
					.map((folder) => {
						folder.expanded = true;

						return folder.id;
					}),
			);
		}

		// build our missing last updated tree

		return [
			...new Map(
				folders
					// This is necessary to ensure that the matterFolder takes precedence over the businessCustomFolder when
					// inside a matter.
					.sort((a, b) => {
						if (a.isMatterLevel && !b.isMatterLevel) {
							return 1;
						}
						if (!a.isMatterLevel && b.isMatterLevel) {
							return -1;
						}
						return 0;
					})
					.map((folder) => [`${folder.id}-${folder.parentId}`, folder]),
			).values(),
		]
			.map((folder) => ({
				...DefaultFileListFolder,
				...folder,
			}))
			.filter(
				(folder) =>
					(!activeSearch && folder.matterType.includes(matterType)) ||
					(activeSearch
						? forceFilteredFolders.has(folder.id) || titleSearchFn(folder)
						: this.mode === DOCUMENT_TREE_MODE.MATTER) ||
					!(folder.matterType.length || matterType),
			)
			.sort((prev, curr) =>
				this.sortList(prev, curr, this.sortBy, this.sortOrder),
			);
	}

	private getAncestorFolders(
		folder: FileListFolder,
		folders: FileListFolder[],
	): FileListFolder[] {
		const parentFolders = folders.filter(
			(currentFolder) => currentFolder.id === folder?.parentId,
		);

		return parentFolders.concat(
			...parentFolders.map((parentFolder) =>
				this.getAncestorFolders(parentFolder, folders),
			),
		);
	}

	/**
	 * Sort method for documents and folders in ascending or descending order on a selected property.
	 * @param prev
	 * @param curr
	 * @param field
	 * @param order
	 * @private
	 */
	private sortList(
		prev: MatterDocument | FileListFolder,
		curr: MatterDocument | FileListFolder,
		field: FileListSortColumn,
		order: FileListSortOrder,
	): number {
		let currentField;
		let previousField;

		// lasteditedby falls back to author
		if (field === 'lastEditedBy') {
			currentField = curr[field];
			previousField = prev[field];
		} else {
			currentField = curr[field];
			previousField = prev[field];
		}

		if (typeof currentField === 'string') {
			currentField = currentField.toLowerCase();
		}
		if (typeof previousField === 'string') {
			previousField = previousField.toLowerCase();
		}

		if (order === 'asc') {
			return previousField < currentField
				? -1
				: previousField > currentField
					? 1
					: 0;
		}

		return previousField > currentField
			? -1
			: previousField < currentField
				? 1
				: 0;
	}

	/**
	 * Ensures only documents and folders that are in the current folder are visible, filtered, and sorted.
	 * @private
	 */
	private updateFileList(): void {
		const allFolders = this.folders;
		const activeSearch = this.filter?.trim() || '';
		const FilteredDocuments = this.filterDocuments(
			this.folderDocuments,
			this.matterType,
			activeSearch,
		);
		const FolderState: FileListFolder[] = allFolders.map(
			(folder) =>
				({
					...folder,
					expanded:
						this.filteredFolders.find((subFolder) => subFolder.id === folder.id)
							?.expanded ??
						this.matterFoldersService.expandedFolders.includes(folder.id),
				}) as FileListFolder,
		);

		// create an object to hold folderId <=> lastUpdated object
		const lastUpdatedMap = new Map<string, { time: number; by: string }>();

		// populate our last updated folder map with all non root documents
		// that have an updatedAt property set with the largest values
		this.documents
			.filter((document) => document.folderId)
			.forEach(({ folderId, updatedAt, lastEditedBy, author, createdAt }) => {
				const savedTime = lastUpdatedMap.get(folderId)?.time || 0;
				const mostRecentTime = Math.max(updatedAt, createdAt, 0);
				if (updatedAt && mostRecentTime > savedTime) {
					lastUpdatedMap.set(folderId, {
						time: mostRecentTime,
						by: lastEditedBy || author,
					});
				}
			});

		// run through all of our folders
		allFolders.forEach((folder) => {
			// find all of this folders descendants
			const allDescendants = getFolderDescendantFolders(folder, allFolders);
			const lastUpdateTime = lastUpdatedMap.get(folder.id)?.time || 0;

			// find the max value row that matches
			const biggestMatch = allDescendants
				.map((descendantFolder) => lastUpdatedMap.get(descendantFolder.id))
				.filter((lastMap) => !!lastMap?.time)
				.filter((lastMap) => lastMap.time > lastUpdateTime)
				.sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
				.pop();

			if (biggestMatch) {
				lastUpdatedMap.set(folder.id, biggestMatch);
			}
		});

		this.filteredDocuments = FilteredDocuments.map((document) => ({
			...document,
			folderId: document.folderId
				? document.folderId
				: document.groupType
					? allFolders.find((folder) => folder.type === document.groupType)?.id
					: '',
		})).filter((document) => document.folderId === this.currentFolderId);

		// now, apply our filters, make changes to pull in the last edited
		// data, and apply our sorts to the folders
		this.filteredFolders = this.filterFolders(
			FolderState,
			this.matterType,
			activeSearch,
			FilteredDocuments,
		)
			.map((folder) => {
				const lastUpdateValue = lastUpdatedMap.get(folder.id) || {
					time: null,
					by: null,
				};
				const children = [
					folder,
					...getFolderDescendantFolders(folder, FolderState),
				]
					.map(
						(descendantFolder) =>
							FilteredDocuments.filter(
								(doc) => doc.folderId === descendantFolder.id,
							).length,
					)
					.reduce((p, c) => p + c, 0);

				return {
					...folder,
					children,
					updatedAt: lastUpdateValue.time,
					author: lastUpdateValue.by || folder.author,
				};
			})
			.filter((folder) => folder.parentId === this.currentFolderId)
			.sort((p, c) => this.sortList(p, c, this.sortBy, this.sortOrder));

		this.resetFolderItemGroups(this.filteredFolders);
		this.resetFileItemGroups(this.filteredDocuments);
	}

	private resetFolderItemGroups(folders: FileListFolder[]) {
		const folderMap = (this.folderItemGroups = {});
		folders.forEach((folder) => {
			folderMap[folder.id] = this.getFolderKebabItemGroups(folder);
		});
	}

	private resetFileItemGroups(files: MatterDocument[]) {
		const fileMap = (this.fileItemGroups = {});
		files.forEach((document) => {
			fileMap[document.id] = this.getFileKebabItemGroups(document);
		});
	}

	get selectedDocumentIds(): Set<string> {
		return (
			DocumentTreeWordComponent.documentSelection ||
			(DocumentTreeWordComponent.documentSelection = new Set())
		);
	}

	set selectedDocumentIds(documentIds: Set<string>) {
		DocumentTreeWordComponent.documentSelection = documentIds;
		this.emitSelectedDocuments();
	}

	onDocumentDragStart(evt: DragEvent, document: MatterDocument): void {
		const selected = this.selectedDocumentIds;
		// make sure it's selected first
		if (!selected.has(document.id)) {
			this.toggleFileSelection(document);
		}
		this.draggedDocuments = [
			...this.documents.filter(
				(doc) => selected.has(doc.id) || doc.id === document.id,
			),
		];
		this.documentIdsBeingDragged = new Set(
			this.draggedDocuments.map((doc) => doc.id),
		);
	}

	onDocumentDragEnd(): void {
		this.clearDragData();
	}

	onFolderClick(evt: Event, folder: FileListFolder): void {
		this.toggleFolderExpansion(folder);
	}

	onFolderKebabClick(evt: Event): void {
		evt.stopImmediatePropagation();
	}

	onFolderKebabSelect(
		{ value }: DropdownOption<FOLDER_ACTIONS>,
		folder: FileListFolder,
	): void {
		switch (value) {
			case FOLDER_ACTIONS.ADD_DOCUMENT:
				if (this.mode === DOCUMENT_TREE_MODE.MANAGE) {
					this.emitAddDocument(folder.id);
				} else {
					this.emitAddDocumentFromTemplate(folder.id);
				}
				break;
			case FOLDER_ACTIONS.ADD_BLANK_DOCUMENT:
				this.emitAddBlankDocument(folder.id);
				break;
			case FOLDER_ACTIONS.ADD_FOLDER:
				this.emitAddFolder(folder.id);
				break;
			case FOLDER_ACTIONS.EDIT_FOLDER:
				this.emitEditFolder(folder.id);
				break;
			case FOLDER_ACTIONS.DELETE_FOLDER:
				this.emitDeleteFolder(folder.id);
				break;
			case FOLDER_ACTIONS.TOGGLE_EXPAND:
				this.toggleFolderExpansion(folder);
				break;
			case FOLDER_ACTIONS.COLLAPSE_AND_UNSET_ALL:
				this.collapseAndUsetExpandAll();
				break;
			default:
				console.warn('invalid folder action clicked', { value, folder });
		}
	}

	onFolderDragEnter(evt: DragEvent, folder: FileListFolder): void {
		this.activeHoveringFolderId = folder.id;
		// does the folder have child folders??
		if (
			!folder.expanded &&
			(this.forceExpandOnDragHover || this.doesFolderHaveSubFolders(folder))
		) {
			setTimeout(() => {
				if (
					this.activeHoveringFolderId &&
					this.activeHoveringFolderId === folder?.id &&
					!folder.expanded
				) {
					this.toggleFolderExpansion(folder);
					this.activeHoveringFolderId = '';
				}
			}, this.folderHoverToExpandDelayMs);
		}
	}

	onFolderDragLeave(): void {
		// stub for now, likely will need it
	}

	onFolderDragOver(evt: DragEvent): void {
		evt.preventDefault();
	}

	onFolderDrop(evt: DragEvent, folder: Folder): void {
		evt.preventDefault();
		const { canMoveDocument, canMoveDocumentToFolder } = this;
		const candidates = this.draggedDocuments
			.filter((doc) => this.isDocumentInDifferentFolder(doc, folder.id))
			.filter((doc) => canMoveDocument(doc))
			.filter((doc) => canMoveDocumentToFolder(doc, folder));

		if (candidates.length) {
			this.doMoveDocumentsToFolder(candidates, folder.id);
			this.clearDragData();
		}
	}

	onFileDragOver(evt: Event): void {
		evt.preventDefault();
	}

	onFileDrop(evt: DragEvent, { folderId }: MatterDocument): void {
		evt.preventDefault();
		const { canMoveDocument, canMoveDocumentToFolder } = this;
		const destinationFolder = this.folders.find(
			(folder) => folder.id === folderId,
		);
		const docs = this.draggedDocuments?.filter(
			(doc) =>
				canMoveDocument(doc) &&
				canMoveDocumentToFolder(doc, destinationFolder) &&
				this.isDocumentInDifferentFolder(doc, folderId),
		);

		if (docs?.length) {
			this.doMoveDocumentsToFolder(docs, folderId);
			this.clearDragData();
		}
	}

	onFileKebabClick(evt: Event): void {
		evt.stopImmediatePropagation();
	}

	onFileClick(evt: Event, document: MatterDocument): void {
		const wasShiftPressed = (evt as MouseEvent)?.shiftKey;

		// the single click event fires after the first of the 2 clicks
		// so, we need to prevent it from firing until we're certain the
		// double click isn't firing
		this.isSingleClicking = true;
		setTimeout(() => {
			if (this.isSingleClicking) {
				if (
					wasShiftPressed &&
					this.lastFileClicked &&
					!this.isDocumentInDifferentFolder(
						document,
						this.lastFileClicked.folderId,
					)
				) {
					// find all the files in our directory
					const candidates = this.filteredDocuments.filter(
						(doc) => (doc.folderId || '') === (document.folderId || ''),
					);
					let startIndex = candidates.indexOf(this.lastFileClicked);
					let endIndex = candidates.indexOf(document);

					// we need to handle reverse selections
					if (startIndex > endIndex) {
						const originalStartIndex = startIndex; // bookmark this
						startIndex = endIndex++; // offset it
						endIndex = originalStartIndex;
					} else {
						endIndex++;
					}

					// now, pull our range and emit selection
					const range = candidates.slice(startIndex, endIndex);
					this.toggleMultipleFileSelection(range.map((doc) => doc.id));

					// and clear ourself
					this.lastFileClicked = null;
				} else {
					this.toggleFileSelection(document);

					// if we manually selected this, ensure we bookmark it for possible use above
					this.lastFileClicked = this.isDocumentSelected(document)
						? document
						: null;
				}
			}
		}, this.doubleClickDelayMs);
	}

	onFileDoubleClick(
		evt: Event,
		{ id, folderId, context }: MatterDocument,
	): void {
		this.isSingleClicking = false; // cancel our 1x click timeout
		this.lastFileClicked = null; // clear our our last clicked, its not needed
		this.emitEditDocument({
			context,
			folderId,
			documentId: id,
		});
	}

	onFileKebabSelect(
		{ value }: DropdownOption<FILE_ACTIONS>,
		{ id, folderId }: MatterDocument,
	): void {
		switch (value) {
			case FILE_ACTIONS.COPY:
				this.emitCopyDocument(id);
				break;
			case FILE_ACTIONS.EDIT_TEMPLATE:
				this.emitEditDocument({ documentId: id, folderId });
				break;
			case FILE_ACTIONS.EDIT_SETTINGS:
				this.editDocumentSettings.emit(id);
				break;
			case FILE_ACTIONS.DELETE:
				this.emitDeleteDocument(id);
				break;
			case FILE_ACTIONS.MOVE:
				this.emitMoveDocument(id);
				break;
			case FILE_ACTIONS.RESET:
				this.emitResetDocument(id);
				break;
			default:
				console.warn('invalid file action selected', { value, id, folderId });
		}
	}

	private doMoveDocumentsToFolder(
		docs: MatterDocument[],
		folderId: string,
	): void {
		if (docs.length) {
			this.moveDocumentsToFolder.emit({
				documents: docs,
				folderId,
			});
		}
	}

	private clearDragData() {
		this.draggedDocuments = [];
		this.documentIdsBeingDragged = new Set();
		this.cdr.detectChanges();
	}

	private isDocumentSelected({ id }: MatterDocument): boolean {
		return this.selectedDocumentIds.has(id);
	}

	/**
	 * determines if the target folder is different then the
	 * folder that the document being dragged exists in
	 * @param folderId folder id of the destination
	 * @returns boolean true
	 */
	private isDocumentInDifferentFolder(
		document: MatterDocument,
		folderId: string,
	): boolean {
		const originFolderId = document.folderId || '';
		const targetFolderId = folderId || '';

		return originFolderId !== targetFolderId;
	}

	private getFileKebabItemGroups(
		document: MatterDocument,
	): { items: DropdownOption<FILE_ACTIONS>[] }[] {
		const { canMoveDocument } = this;
		const items: DropdownOption<FILE_ACTIONS>[] = [
			{
				icon: ICON.EDIT,
				name: 'Edit',
				value: FILE_ACTIONS.EDIT_TEMPLATE,
			},
			{
				icon: ICON.COPY,
				name: 'Copy',
				value: FILE_ACTIONS.COPY,
			},
		];

		if (this.mode === DOCUMENT_TREE_MODE.MANAGE) {
			items.push({
				icon: ICON.GEAR,
				name: 'Settings',
				value: FILE_ACTIONS.EDIT_SETTINGS,
			});
		}

		if (canMoveDocument(document)) {
			items.push({
				icon: ICON.MOVE,
				name: 'Move',
				value: FILE_ACTIONS.MOVE,
			});
		}

		if (this.mode === DOCUMENT_TREE_MODE.MATTER && document.source) {
			items.push({
				icon: ICON.REFRESH,
				name: 'Reset',
				value: FILE_ACTIONS.RESET,
			});
		}

		if (
			(this.mode === DOCUMENT_TREE_MODE.MANAGE &&
				!(document as unknown as BusinessDocumentV2).isBasedOnCloserTemplate) ||
			(this.mode === DOCUMENT_TREE_MODE.MATTER && !document.alwaysExists)
		) {
			items.push({
				classes: ['text-red-500'],
				icon: ICON.DELETE,
				name: 'Delete',
				value: FILE_ACTIONS.DELETE,
			});
		}

		return [{ items }];
	}

	private getFolderKebabItemGroups(folder: FileListFolder): SelectionGroup[] {
		const { canCreateFolder } = this;
		const { documentsExpandAll } = this;
		const items: DropdownOption<FOLDER_ACTIONS>[] = [];
		const topItems: DropdownOption<FOLDER_ACTIONS>[] = [];
		const bottomItems: DropdownOption<FOLDER_ACTIONS>[] = [];

		if (documentsExpandAll) {
			items.push({
				name: 'Collapse all folders',
				value: FOLDER_ACTIONS.COLLAPSE_AND_UNSET_ALL,
			});
		} else {
			topItems.push({
				get name() {
					return folder.expanded ? 'Collapse folder' : 'Expand folder';
				},
				value: FOLDER_ACTIONS.TOGGLE_EXPAND,
			});
		}

		if (!folder.type) {
			bottomItems.push({
				name: 'Rename',
				value: FOLDER_ACTIONS.EDIT_FOLDER,
			});
		}

		if (this.mode === DOCUMENT_TREE_MODE.MANAGE) {
			items.push({
				name: 'Blank document',
				value: FOLDER_ACTIONS.ADD_DOCUMENT,
			});
		} else {
			items.push({
				name: 'Document from templates',
				value: FOLDER_ACTIONS.ADD_DOCUMENT,
			});
			items.push({
				name: 'Blank document',
				value: FOLDER_ACTIONS.ADD_BLANK_DOCUMENT,
			});
		}

		if (canCreateFolder(folder)) {
			items.push({
				name: 'Subfolder',
				value: FOLDER_ACTIONS.ADD_FOLDER,
			});
		}

		if (
			(this.mode === DOCUMENT_TREE_MODE.MANAGE &&
				folder.author &&
				!folder.type) ||
			(this.mode === DOCUMENT_TREE_MODE.MATTER &&
				!folder.type &&
				FolderDeletable(folder.id, this.folders, this.folderDocuments))
		) {
			bottomItems.push({
				classes: ['text-red-500'],
				name: 'Delete',
				value: FOLDER_ACTIONS.DELETE_FOLDER,
			});
		}

		const finalItems: SelectionGroup[] = [{ items, title: 'Add to folder:' }];
		if (topItems.length) {
			finalItems.unshift({ items: topItems });
		}

		if (bottomItems.length) {
			finalItems.push({ items: bottomItems });
		}

		return finalItems;
	}

	private doesFolderHaveSubFolders({ id }: FileListFolder): boolean {
		return this.folders.some((folder) => folder.parentId === id);
	}
}
